import moment from 'moment'
import { useState } from 'react'
import { Image, Modal } from 'react-bootstrap'

interface IReverificationsTable {
    tableData: any
    pageOffset?: number
}

interface IPreviewItem {
    enrolled: string
    live: string
    updated?: string
    name: string
}

const STATUS_STYLES: Record<string, { bg: string; color: string; label: string }> = {
    matched:     { bg: '#d1fae5', color: '#065f46', label: 'Matched' },
    not_matched: { bg: '#fee2e2', color: '#991b1b', label: 'Not Matched' },
    null:        { bg: '#f3f4f6', color: '#6b7280', label: '—' },
}

function StatusPill({ value }: { value: string | null }) {
    const key = value ?? 'null'
    const s = STATUS_STYLES[key] ?? STATUS_STYLES['null']
    return (
        <span style={{
            display: 'inline-block',
            padding: '3px 10px',
            borderRadius: 20,
            fontSize: 11,
            fontWeight: 600,
            background: s.bg,
            color: s.color,
            whiteSpace: 'nowrap',
        }}>
            {s.label}
        </span>
    )
}

function ThumbImg({ src, alt }: { src?: string; alt: string }) {
    if (!src) return (
        <div style={{
            width: 44, height: 44, borderRadius: 8,
            background: '#f3f4f6', display: 'flex', alignItems: 'center', justifyContent: 'center',
            border: '1px solid #e5e7eb', flexShrink: 0,
        }}>
            <span style={{ fontSize: 18, color: '#d1d5db' }}>👤</span>
        </div>
    )
    return (
        <img
            src={src}
            alt={alt}
            style={{ width: 44, height: 44, objectFit: 'cover', borderRadius: 8, border: '1px solid #e5e7eb', flexShrink: 0 }}
        />
    )
}

export default function ReverificationTable({ tableData, pageOffset = 0 }: IReverificationsTable) {
    const [preview, setPreview] = useState<IPreviewItem | null>(null)

    const openPreview = (item: any) => {
        setPreview({
            enrolled: item?.studentId?.avatar,
            live: item?.verificationId?.userLivePhoto,
            updated: item?.verificationId?.userLivePhotoUpdated,
            name: item?.counsellingUser?.name || 'Candidate',
        })
    }

    return (
        <>
            {/* Photo comparison modal */}
            <Modal show={!!preview} onHide={() => setPreview(null)} centered size="lg">
                <Modal.Header style={{ borderBottom: '1px solid #e5e7eb', background: '#f8fafc', padding: '16px 20px' }}>
                    <div>
                        <div style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Photo Comparison</div>
                        <div style={{ fontSize: 15, fontWeight: 700, color: '#111827', textTransform: 'uppercase' }}>{preview?.name}</div>
                    </div>
                    <button
                        onClick={() => setPreview(null)}
                        style={{ background: 'none', border: 'none', fontSize: 22, color: '#9ca3af', cursor: 'pointer', lineHeight: 1, marginLeft: 'auto' }}
                    >×</button>
                </Modal.Header>
                <Modal.Body style={{ background: '#fff', padding: 24 }}>
                    <div style={{ display: 'flex', gap: 20, justifyContent: 'center' }}>
                        {[
                            { label: 'Enrolled Photo', src: preview?.enrolled },
                            { label: 'Live Exam Photo', src: preview?.live },
                            ...(preview?.updated ? [{ label: 'Reverification Photo', src: preview.updated }] : []),
                        ].map((p, i, arr) => (
                            <div key={p.label} style={{ flex: 1, textAlign: 'center' }}>
                                <div style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>{p.label}</div>
                                <Image
                                    src={p.src}
                                    style={{ width: '100%', maxWidth: 220, height: 240, objectFit: 'cover', borderRadius: 12, border: '2px solid #e5e7eb', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}
                                />
                                {i < arr.length - 1 && (
                                    <div style={{ position: 'absolute', top: 0, right: -10, height: '100%', width: 1, background: '#e5e7eb' }} />
                                )}
                            </div>
                        ))}
                    </div>
                </Modal.Body>
            </Modal>

            {/* Table */}
            <div style={{ overflowX: 'auto', marginTop: 4 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead>
                        <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e5e7eb' }}>
                            {['#', 'CET Reg. No', 'Ph 1 R.No', 'Candidate Name', 'Institute Code', 'Location', 'Photos', 'Fingerprint', 'Face', 'Verified At'].map(h => (
                                <th key={h} style={{
                                    padding: '10px 12px',
                                    textAlign: 'left',
                                    fontSize: 11,
                                    fontWeight: 700,
                                    color: '#6b7280',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.06em',
                                    whiteSpace: 'nowrap',
                                }}>
                                    {h}
                                </th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {tableData?.length > 0 ? tableData.map((item: any, index: number) => (
                            <tr
                                key={item?._id || index}
                                style={{ borderBottom: '1px solid #f3f4f6', transition: 'background 0.1s' }}
                                onMouseEnter={e => (e.currentTarget.style.background = '#fafafa')}
                                onMouseLeave={e => (e.currentTarget.style.background = '')}
                            >
                                <td style={tdStyle}>
                                    <span style={{ color: '#9ca3af', fontSize: 12 }}>{pageOffset + index + 1}</span>
                                </td>
                                <td style={tdStyle}>
                                    <span style={{ fontFamily: 'monospace', fontSize: 12, color: '#374151', fontWeight: 600 }}>
                                        {item?.counsellingUser?.capApplicationNumber || '—'}
                                    </span>
                                </td>
                                <td style={tdStyle}>
                                    <span style={{ fontFamily: 'monospace', fontSize: 12, color: '#374151' }}>
                                        {item?.counsellingUser?.rollNo || '—'}
                                    </span>
                                </td>
                                <td style={tdStyle}>
                                    <span style={{ fontWeight: 600, color: '#111827', textTransform: 'uppercase', fontSize: 12 }}>
                                        {item?.counsellingUser?.name || '—'}
                                    </span>
                                </td>
                                <td style={tdStyle}>
                                    <span style={{ background: '#eff6ff', color: '#1d4ed8', borderRadius: 6, padding: '2px 8px', fontSize: 11, fontWeight: 600 }}>
                                        {item?.counsellingUser?.allotedInstituteCode || '—'}
                                    </span>
                                </td>
                                <td style={{ ...tdStyle, maxWidth: 160 }}>
                                    <span style={{ fontSize: 12, color: '#6b7280', textTransform: 'capitalize' }}>
                                        {item?.counsellingUser?.allotedInstituteName || '—'}
                                    </span>
                                </td>
                                <td style={tdStyle}>
                                    <div
                                        style={{ display: 'flex', gap: 4, alignItems: 'center', cursor: 'pointer' }}
                                        onClick={() => openPreview(item)}
                                        title="Click to compare photos"
                                    >
                                        <ThumbImg src={item?.studentId?.avatar} alt="enrolled" />
                                        {item?.verificationId?.userLivePhoto && (
                                            <ThumbImg src={item.verificationId.userLivePhoto} alt="live" />
                                        )}
                                        {item?.verificationId?.userLivePhotoUpdated && (
                                            <ThumbImg src={item.verificationId.userLivePhotoUpdated} alt="reverification" />
                                        )}
                                    </div>
                                </td>
                                <td style={tdStyle}>
                                    <StatusPill value={item?.fingerprintStatus} />
                                </td>
                                <td style={tdStyle}>
                                    <StatusPill value={item?.facialStatus} />
                                </td>
                                <td style={tdStyle}>
                                    <span style={{ fontSize: 12, color: '#374151', whiteSpace: 'nowrap' }}>
                                        {item?.updatedAt ? moment(item.updatedAt).format('DD MMM YY, HH:mm') : '—'}
                                    </span>
                                </td>
                            </tr>
                        )) : (
                            <tr>
                                <td colSpan={10} style={{ textAlign: 'center', padding: '48px 0', color: '#9ca3af', fontSize: 14 }}>
                                    No records found
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </>
    )
}

const tdStyle: React.CSSProperties = {
    padding: '10px 12px',
    verticalAlign: 'middle',
}
